import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Phone, Clock, MessageCircle, Play, Star,
  Leaf, TreeDeciduous, Scissors, Shovel, ChevronRight, Mail,
  Sun, CheckCircle2, Droplets
} from 'lucide-react';

const DemoTuingeluk = () => {
  const [showVideo, setShowVideo] = useState(false);

  const business = {
    name: "Tuingeluk",
    tagline: "Professioneel tuinonderhoud in België",
    website: "www.tuingeluk.be",
    hero: "https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&q=72&auto=format",
    rating: 4.8,
    reviews: 203,
    address: "Groenstraat 45, 2000 Antwerpen",
    phone: "+32 3 123 45 67",
    whatsapp: "3231234567",
    email: "info@tuingeluk.be",
    hours: "Ma-Vr: 07:00-18:00 | Za: 08:00-13:00",
    videoId: "dQw4w9WgXcQ",
  };

  const services = [
    { icon: Scissors, name: 'Snoeien', desc: 'Hagen & struiken' },
    { icon: TreeDeciduous, name: 'Beplanting', desc: 'Tuinaanleg & design' },
    { icon: Leaf, name: 'Gazonzorg', desc: 'Maaien & bemesten' },
    { icon: Shovel, name: 'Onderhoud', desc: 'Seizoensonderhoud' },
  ];

  const priceList = [
    { service: 'Tuinonderhoud', unit: 'per uur', price: '35' },
    { service: 'Gazon maaien', unit: 'per 100m²', price: '25' },
    { service: 'Haag snoeien', unit: 'per meter', price: '8' },
    { service: 'Onkruid verwijderen', unit: 'per uur', price: '30' },
    { service: 'Tuinafval afvoer', unit: 'per m³', price: '45' },
    { service: 'Seizoenscontract', unit: 'per jaar', price: 'vanaf 599' },
  ];

  const uspPoints = [
    'Gratis tuininspectie',
    'Vakkundig advies',
    'Ecologisch verantwoord',
    'Flexibele planning',
  ];

  const seasons = [
    { name: 'Lente', icon: Sun, tasks: 'Bemesting, snoeien, planten' },
    { name: 'Zomer', icon: Droplets, tasks: 'Maaien, bewateren, onkruid' },
    { name: 'Herfst', icon: Leaf, tasks: 'Bladruimen, winterklaar' },
    { name: 'Winter', icon: TreeDeciduous, tasks: 'Snoeien, planning' },
  ];

  const whatsappMessage = encodeURIComponent("Hallo Tuingeluk! Ik wil graag een vrijblijvende offerte voor tuinonderhoud.");

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Hero */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={business.hero} 
          alt={`${business.name} - Professioneel tuinonderhoud in België`}
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-green-50 via-transparent to-transparent" />
        
        {/* Logo */}
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-20 h-20 rounded-2xl border-4 border-white overflow-hidden shadow-xl bg-green-600 flex items-center justify-center">
            <Leaf className="text-white" size={36} />
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="pt-14 pb-8 px-6 max-w-lg mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{business.name}</h1>
          <p className="text-gray-500 text-sm mb-2">{business.tagline}</p>
          <a href={`https://${business.website}`} target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm font-medium">
            {business.website}
          </a>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={14} 
                  className={i < Math.floor(business.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} 
                />
              ))}
            </div>
            <span className="text-gray-900 font-medium">{business.rating}</span>
            <span className="text-gray-400">({business.reviews} reviews)</span>
          </div>
        </motion.div>

        {/* WhatsApp CTA */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          href={`https://wa.me/${business.whatsapp}?text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 bg-[#25D366] text-white py-4 px-6 rounded-2xl font-semibold mb-4 shadow-lg shadow-green-200 hover:shadow-xl transition-shadow"
          data-testid="whatsapp-btn"
        >
          <MessageCircle size={24} fill="white" />
          Gratis tuininspectie aanvragen
        </motion.a>

        {/* Email alternative */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          href={`mailto:${business.email}`}
          className="flex items-center justify-center gap-2 text-green-600 mb-6 text-sm"
        >
          <Mail size={16} />
          Of mail naar {business.email}
        </motion.a>

        {/* Video Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Play size={18} className="text-green-600" />
            Onze werkwijze
          </h2>
          <div 
            className="relative aspect-video rounded-2xl overflow-hidden bg-gray-100 cursor-pointer group"
            onClick={() => setShowVideo(true)}
          >
            {showVideo ? (
              <iframe
                src={`https://www.youtube.com/embed/${business.videoId}?autoplay=1`}
                title="Tuingeluk video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <img 
                  src={business.hero}
                  alt={`${business.name} video thumbnail - Bekijk onze tuinonderhoud diensten`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                    <Play className="text-green-600 ml-1" size={28} fill="currentColor" />
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* USP Points */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-green-50 rounded-2xl p-4 mb-6"
        >
          <div className="grid grid-cols-2 gap-2">
            {uspPoints.map((point, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" />
                {point}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 gap-3 mb-6"
        >
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <service.icon className="text-green-600 mb-2" size={24} />
              <h3 className="font-semibold text-gray-900 text-sm">{service.name}</h3>
              <p className="text-gray-500 text-xs">{service.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Seasonal Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-6"
        >
          <h2 className="text-lg font-bold text-gray-900 mb-3">Seizoensgebonden zorg</h2>
          <div className="grid grid-cols-2 gap-2">
            {seasons.map((season, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 border border-green-200"
              >
                <div className="flex items-center gap-2 mb-1">
                  <season.icon size={16} className="text-green-700" />
                  <span className="font-semibold text-green-800 text-sm">{season.name}</span>
                </div>
                <p className="text-green-700 text-xs">{season.tasks}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Price List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-6"
        >
          <h2 className="text-lg font-bold text-gray-900 mb-3">Tarieven</h2>
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
            {priceList.map((item, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-4 ${
                  index !== priceList.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <div>
                  <p className="font-medium text-gray-900">{item.service}</p>
                  <p className="text-gray-400 text-sm">{item.unit}</p>
                </div>
                <span className="font-bold text-green-600 text-lg">€{item.price}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Info Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-3 mb-6"
        >
          <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Clock className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Bereikbaar</p>
              <p className="text-gray-900 font-medium">{business.hours}</p>
            </div>
          </div>
          
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(business.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100"
          >
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MapPin className="text-green-600" size={20} />
            </div>
            <div className="flex-grow">
              <p className="text-gray-400 text-sm">Werkgebied</p>
              <p className="text-gray-900 font-medium">Antwerpen & omgeving</p>
            </div>
            <ChevronRight className="text-gray-400" size={20} />
          </a>

          <a 
            href={`tel:${business.phone}`}
            className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100"
          >
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Phone className="text-green-600" size={20} />
            </div>
            <div className="flex-grow">
              <p className="text-gray-400 text-sm">Telefoon</p>
              <p className="text-gray-900 font-medium">{business.phone}</p>
            </div>
            <ChevronRight className="text-gray-400" size={20} />
          </a>
        </motion.div>

        {/* Bottom CTA */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          href={`https://wa.me/${business.whatsapp}?text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-6 rounded-xl font-semibold mb-6"
        >
          <Leaf size={18} />
          Plan tuinbezoek
        </motion.a>

        {/* Netblitz CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 text-white text-center mb-4"
        >
          <p className="text-blue-100 text-sm mb-1">Ook zo'n website voor jouw bedrijf?</p>
          <p className="font-bold text-xl mb-3">Vanaf €499</p>
          <div className="flex gap-2">
            <a 
              href="https://wa.me/32484448854?text=Hallo%20Netblitz!%20Ik%20heb%20interesse%20in%20een%20website%20zoals%20Tuingeluk."
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-[#25D366] py-2 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
            >
              <MessageCircle size={16} fill="white" />
              WhatsApp
            </a>
            <a 
              href="mailto:hello@netblitz.eu?subject=Website%20aanvraag%20-%20zoals%20Tuingeluk"
              className="flex-1 bg-white/20 py-2 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
            >
              <Mail size={16} />
              E-mail
            </a>
          </div>
        </motion.div>

        {/* Powered by */}
        <div className="text-center">
          <a href="https://netblitz.eu" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-xs hover:text-green-600 transition-colors">
            Powered by <span className="font-medium">Netblitz</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DemoTuingeluk;
